<template>
  <div ref="chartContainer" style="width: 100%; height: 100%;">
    <highcharts class="mt-4" :options="options" ref="aChart"
     style="width: 100%; height: 100%;"></highcharts>
  </div>
</template>
<script>
import { mapState } from 'vuex';

export default {
  name: 'CumulativeProductionCount',
  props: {
    thisMachines: {
      type: Array,
      required: true,
    },
  },
  mounted() {
    this.chart = this.$refs.aChart.chart;
    this.createSeries();
    this.initResizeObserver();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  data() {
    return {
      resizeObserver: null,
      chart: null,
      options: {
        chart: {
          type: 'column',
          style: {
            fontFamily: 'Poppins, sans-serif',
          },
        },
        title: {
          text: 'Cumulative Production Count',
          style: {
            color: '',
          },
        },
        plotOptions: {
          series: {
            grouping: true,
            borderWidth: 0,
          },
        },
        legend: {
          enabled: true,
          align: 'center',
          verticalAlign: 'bottom',
          layout: 'horizontal',
          itemStyle: {
            color: '#333',
            fontWeight: 'bold',
          },
        },
        tooltip: {
          shared: true,
          /* eslint-disable */
          formatter: function () {
            let s = `<b>${this.x}</b><br/>`;
            this.points.forEach(point => {
              if (point.y !== 0) { // 👈 hide zeros
                s += `<span style="color:${point.series.color}">●</span> ${point.series.name}: <b>${point.y}</b><br/>`;
              }
            });
            return s;
          }
          /* eslint-enable */
        },

        xAxis: {
          type: 'category',
          labels: {
            style: {
              color: '',
            },
          },
        },
        yAxis: [
          {
            title: {
              text: null,
            },
            labels: {
              style: {
                color: '',
              },
            },
          },
        ],
        series: [{}],
      },
    };
  },
  computed: {
    ...mapState('helper', ['isDark']),
  },
  methods: {
    roundOff(val) {
      if (val) {
        return Math.round((val + Number.EPSILON) * 100) / 100;
      }
      return 0;
    },
    getPartColor(part) {
      if (part.includes('GEN3')) return 'rgba(3, 139, 190)';
      if (part.includes('EBNA')) return 'rgba(95, 245, 242)';
      if (part.includes('Cylinder Block')) return 'rgba(255, 140, 0)';
      if (part.includes('Cylinder Head')) return 'rgba(153, 50, 204)';
      if (part.includes('Crank Shaft')) return 'rgba(100, 149, 237)';
      if (part.includes('NA')) return 'rgba(146, 149, 158)';
      // random color for others
      return `hsl(${Math.floor(Math.random() * 360)}, 70%, 50%)`;
    },
    createSeries() {
      // Unique machine names (x-axis categories)
      const categories = [...new Set(this.thisMachines.map((m) => m.machinename))];
      this.options.xAxis.categories = categories;

      // Collect all unique part names from partnames object
      const partNamesSet = new Set();
      this.thisMachines.forEach((m) => {
        Object.keys(m.partnames).forEach((part) => partNamesSet.add(part));
      });
      const partNames = Array.from(partNamesSet).sort();

      // Stacked column series by partname
      const producedSeries = partNames.map((part) => ({
        name: part,
        type: 'column',
        data: categories.map((machine) => {
          const item = this.thisMachines.find((m) => m.machinename === machine);
          return item && item.partnames[part] ? item.partnames[part] : '';
        }),
        stack: 'Produced',
        color: this.getPartColor(part),
      }));

      // Target line (kept same)
      const targetSeries = {
        name: 'Target',
        type: 'line',
        data: categories.map((machine) => {
          const item = this.thisMachines.find((m) => m.machinename === machine);
          return item ? item.target : 0;
        }),
        color: '#1CA4D7',
        marker: {
          enabled: true,
          radius: 3,
        },
        zIndex: 5,
        dataLabels: {
          enabled: true,
          style: { fontWeight: 'bold', color: '#000' },
          // eslint-disable-next-line
          formatter: function () {
            return this.y && this.y !== 0 ? this.y : null;
          },
        },
      };

      // Apply stacked series + target
      this.options.series = [...producedSeries, targetSeries];

      // Enable stacking only for columns
      this.options.plotOptions.column = {
        stacking: 'normal',
        dataLabels: {
          enabled: true,
          formatter() {
            return this.y;
          },
        },
      };

      this.$nextTick(() => this.reflowChart());
    },

    initResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        this.reflowChart();
      });
      if (this.$refs.chartContainer) {
        this.resizeObserver.observe(this.$refs.chartContainer);
      }
    },
    reflowChart() {
      this.$nextTick(() => {
        const chartInstance = this.$refs.aChart?.chart;
        if (chartInstance && chartInstance.reflow) {
          chartInstance.reflow();
        }
      });
    },
  },
  watch: {
    thisMachines: {
      deep: true,
      handler() {
        this.createSeries();
      },
    },
    isDark: {
      immediate: true,
      handler(val) {
        const color = val ? '#FFFFFF' : '#333333';
        const labelColor = val ? '#FFFFFF' : '#666666';
        const seriesColor = val ? '#21C77C' : '#354493';
        this.options.title.style.color = color;
        this.options.yAxis[0].labels.style.color = labelColor;
        this.options.xAxis.labels.style.color = labelColor;
        if (this.options.series[0]) {
          this.options.series[0].color = seriesColor;
        }
      },
    },
  },
};
</script>
